import { RootState } from "./store";
import aiservice from "@apis/aiservice";
import { Status } from "@models/common";
import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";

type MessageRole = "USER" | "GPT";

export const GlobalAIFrom = {
  GROBAL: "GROBAL",
  SCRIPT: "SCRIPT",
};

export interface Message {
  id: string;
  role: MessageRole;
  text: string;
  script?: string;
  status?: Status;
  writing?: boolean;
}

interface InitialState {
  from: string;
  open: boolean;
  script?: string;
  remainCount: number;
  messages: Message[];
}

const initialState: InitialState = {
  from: "",
  open: false,
  script: undefined,
  remainCount: 0,
  messages: [],
};

export const getHistoryMessages = createAsyncThunk("globalAi/getHistoryMessages", async () => {
  return await aiservice.getHistoryMessages<
    { id: string; sourceType: MessageRole; extra: string; videoText?: string }[]
  >();
});

export const getRemainCount = createAsyncThunk("globalAi/getRemainCount", async () => {
  return await aiservice.getRemainCount<number>();
});

export const askQuestion = createAsyncThunk(
  "globalAi/askQuestion",
  async ({ content, videoText }: { content: string; videoText?: string }) => {
    return await aiservice.askQuestion<{ id: string }>({ content, videoText });
  },
);

export const getLatestAnswer = createAsyncThunk("globalAi/getLatestAnswer", async () => {
  return await aiservice.getLatestAnswer<{ id: string }>();
});

export const globalSlice = createSlice({
  name: "globalAI",
  initialState,
  reducers: {
    showGlobalAI: (state, action: PayloadAction<{ from: string; script?: string }>) => {
      state.from = action.payload.from;
      state.script = action.payload.script;
      state.open = true;
    },
    hideGlobalAI: (state) => {
      state.from = "";
      state.script = "";
      state.open = false;
    },
    clearScript: (state) => {
      state.script = undefined;
    },
    addLatestQuestion: (state, action: PayloadAction<string>) => {
      const messages = state.messages;
      if (messages.length > 0) {
        const lastMessage = messages[messages.length - 1];
        if (lastMessage.role === "GPT" && lastMessage.status === "failed") {
          state.messages = [
            ...messages.slice(0, messages.length - 1),
            {
              id: "",
              role: "USER",
              text: action.payload,
              script: state.script,
            },
          ];
          return;
        }
      }

      state.messages = [
        ...state.messages,
        {
          id: "",
          role: "USER",
          text: action.payload,
          script: state.script,
        },
      ];
    },
    resetToInit: (state) => {
      Object.keys(initialState).map((key) => {
        // @ts-ignore
        state[key] = initialState[key];
      });
    },
    finishWriting: (state) => {
      const messages = state.messages;

      if (messages.length > 0) {
        const lastMessage = messages[messages.length - 1];
        if (lastMessage.role === "GPT" && lastMessage.writing) {
          lastMessage.writing = false;
        }
        state.messages = [...messages.slice(0, messages.length - 1), lastMessage];
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getHistoryMessages.fulfilled, (state, action) => {
        const messages = action.payload;
        state.messages = messages.map((message) => {
          return {
            id: message.id,
            role: message.sourceType,
            text: message.extra,
            script: message.videoText,
            status: "succeeded",
          };
        });
      })
      .addCase(getRemainCount.fulfilled, (state, action) => {
        state.remainCount = action.payload;
      })
      .addCase(getLatestAnswer.pending, (state) => {
        const messages = state.messages;
        const lastMessage = messages[messages.length - 1];
        if (lastMessage.role === "USER") {
          state.messages = [
            ...state.messages,
            {
              id: "",
              role: "GPT",
              text: "...",
              status: "loading",
            },
          ];
        }
        if (lastMessage.role === "GPT" && lastMessage.status === "failed") {
          state.messages = [
            ...messages.slice(0, messages.length - 1),
            {
              id: "",
              role: "GPT",
              text: "...",
              status: "loading",
            },
          ];
        }
      })
      .addCase(getLatestAnswer.fulfilled, (state, action) => {
        const answer = action.payload as any;
        const messages = state.messages;
        if (answer.status !== 1) {
          state.messages = [
            ...messages.slice(0, messages.length - 1),
            {
              id: answer.messageId,
              role: "GPT",
              text: answer.content,
              status: "succeeded",
              writing: true,
            },
          ];
          state.remainCount = state.remainCount - 1;
        } else {
          const lastMessage = messages[messages.length - 1];
          state.messages = [
            ...messages.slice(0, messages.length - 1),
            {
              ...lastMessage,
              status: "failed",
            },
          ];
        }
      })
      .addCase(getLatestAnswer.rejected, (state) => {
        const messages = state.messages;
        const lastMessage = messages[messages.length - 1];
        state.messages = [
          ...messages.slice(0, messages.length - 1),
          {
            ...lastMessage,
            status: "failed",
          },
        ];
      });
  },
});

export const { showGlobalAI, hideGlobalAI, clearScript, addLatestQuestion, resetToInit, finishWriting } =
  globalSlice.actions;

const globalReducer = globalSlice.reducer;
export default globalReducer;

export const selectIsGenerating = (state: RootState) => {
  const messages = state.globalAISlice.messages;
  if (messages.length > 0) {
    const lastMessage = messages[messages.length - 1];
    if (lastMessage.role === "GPT" && lastMessage.status === "loading") {
      return true;
    }
  }
  return false;
};
